import { Fragment, useState, type FormEvent } from 'react'
import { supportStatusText } from '@billiard/shared'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  EmptyState,
  Field,
  FormError,
  Input,
  PageHead,
  SectionHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { dateTime } from '@/lib/format'

/**
 * Контакты поддержки. Те же значения показывает касса (pages/Support.tsx
 * терминала) — при смене править оба места.
 */
export const SUPPORT_PHONE = '+375 25 783-88-79'
export const SUPPORT_PHONE_RAW = '+375257838879'
export const SUPPORT_TELEGRAM = 'darkwoodsoft_support'
export const SUPPORT_BOT = 'Help_DarkwoodSoft_bot'

const CATEGORY_LABEL: Record<string, string> = {
  question: 'Вопрос по работе',
  problem: 'Что-то не работает',
  idea: 'Пожелание',
  billing: 'Оплата и лицензия'
}

/** Откуда написали обращение (support_tickets.source). */
const SOURCE_LABEL: Record<string, string> = {
  terminal: 'Касса',
  backoffice: 'Бэкофис',
  telegram: 'Telegram'
}

interface SupportMessage {
  id: string
  author: string
  fromAgent: boolean
  body: string
  createdAt: number
  /** Доставка ответа клуба в Help: queued | sent | failed (у ответов агента — null). */
  deliveryStatus?: string | null
}

interface SupportTicket {
  id: string
  source: string
  category: string
  subject: string
  message: string
  authorName: string
  status: string
  helpdeskTicketId: string | null
  helpdeskError: string | null
  helpdeskStatus: string | null
  helpdeskStatusLabel?: string | null
  helpdeskResolved?: boolean | null
  lastReplyAt: number | null
  createdAt: number
  messages: SupportMessage[]
}

/** Задача, поставленная клубу поддержкой (живёт в Help, здесь только показ). */
interface SupportTask {
  id: number
  title: string
  description: string | null
  priority: string
  dueDate: string | null
  createdBy: string | null
}

export function Support(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Помощь"
        title="Поддержка"
        lead="Напишите нам — обращение попадёт в систему поддержки, а ответ придёт в Telegram или по телефону, который вы укажете."
      />
      <ClubGate>
        <SupportBody />
      </ClubGate>
    </>
  )
}

function SupportBody(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<SupportTicket[]>('/v1/support/tickets', query, [query?.clubId])
  const save = useMutate()
  const retry = useMutate()

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [category, setCategory] = useState('question')
  const [contact, setContact] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const [openTicket, setOpenTicket] = useState<string | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const sendReply = useMutate()

  const tasks = useFetch<SupportTask[]>('/v1/support/tasks', query, [query?.clubId])
  const doneTask = useMutate()
  const rows = list.data ?? []
  const taskRows = tasks.data ?? []
  // Застрявшее — и обращения, и ответы клуба: «Отправить повторно» досылает и то и другое.
  const queued =
    rows.filter((r) => r.status !== 'sent').length +
    rows.flatMap((r) => r.messages ?? []).filter((m) => m.deliveryStatus === 'queued').length

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (subject.trim().length < 3) return setValidation('Укажите тему обращения.')
    if (message.trim().length < 10) return setValidation('Опишите вопрос подробнее.')
    setValidation(null)
    const ok = await save.mutate(() =>
      api.post(
        '/v1/support/tickets',
        {
          subject: subject.trim(),
          message: message.trim(),
          category,
          contact: contact.trim() || undefined
        },
        { query }
      )
    )
    if (ok) {
      setSubject('')
      setMessage('')
      setContact('')
      list.reload()
    }
  }

  const onTaskDone = async (task: SupportTask): Promise<void> => {
    const ok = await doneTask.mutate(() =>
      api.post(`/v1/support/tasks/${task.id}/done`, {}, { query })
    )
    if (ok) tasks.reload()
  }

  const onReply = async (ticket: SupportTicket): Promise<void> => {
    const text = replyDraft.trim()
    if (!text) return
    // messageId — ключ идемпотентности: двойной клик или повтор после обрыва
    // связи не задвоит ответ в Help.
    const messageId = crypto.randomUUID()
    const ok = await sendReply.mutate(() =>
      api.post(`/v1/support/tickets/${ticket.id}/messages`, { message: text, messageId }, { query })
    )
    if (ok) {
      setReplyDraft('')
      list.reload()
    }
  }

  const onRetry = async (): Promise<void> => {
    const ok = await retry.mutate(() => api.post('/v1/support/tickets/retry', {}, { query }))
    if (ok) list.reload()
  }

  return (
    <>
      <div className="card" style={{ marginTop: 24 }}>
        <SectionHead title="Связаться напрямую" />
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
          <li>
            Телефон: <a href={`tel:${SUPPORT_PHONE_RAW}`}>{SUPPORT_PHONE}</a>
          </li>
          <li>
            Telegram:{' '}
            <a href={`https://t.me/${SUPPORT_TELEGRAM}`} target="_blank" rel="noreferrer">
              @{SUPPORT_TELEGRAM}
            </a>
          </li>
          <li>
            Бот поддержки:{' '}
            <a href={`https://t.me/${SUPPORT_BOT}`} target="_blank" rel="noreferrer">
              @{SUPPORT_BOT}
            </a>
          </li>
        </ul>
      </div>

      {taskRows.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <SectionHead
            title="Задачи от поддержки"
            meta="Поручения нашего специалиста — отметьте, когда сделаете"
          />
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Задача</th>
                  <th>Кто поставил</th>
                  <th>Срок</th>
                  <th style={{ width: 140 }} />
                </tr>
              </thead>
              <tbody>
                {taskRows.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <div>{t.title}</div>
                      {t.description && <div className="muted">{t.description}</div>}
                    </td>
                    <td>{t.createdBy ?? 'Поддержка'}</td>
                    <td>{t.dueDate ? new Date(t.dueDate).toLocaleDateString('ru-RU') : '—'}</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={doneTask.saving}
                        onClick={() => void onTaskDone(t)}
                      >
                        Выполнено
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <SectionHead title="Новое обращение" />
        <form onSubmit={onSubmit}>
          <Field label="Тема" htmlFor="sup-subject">
            <Input
              id="sup-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Коротко: что случилось"
              maxLength={200}
              required
            />
          </Field>
          <Field label="Категория" htmlFor="sup-category">
            <Select
              id="sup-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {Object.entries(CATEGORY_LABEL).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Описание" htmlFor="sup-message">
            <textarea
              id="sup-message"
              className="input"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Что происходит, где и когда началось"
              maxLength={5000}
              required
            />
          </Field>
          <Field label="Как с вами связаться (необязательно)" htmlFor="sup-contact">
            <Input
              id="sup-contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Телефон, почта или @telegram"
              maxLength={200}
            />
          </Field>
          {(validation ?? save.error) && <FormError>{validation ?? save.error}</FormError>}
          <div style={{ marginTop: 12 }}>
            <Button variant="primary" type="submit" disabled={save.saving}>
              {save.saving ? <span className="spin" /> : null}
              Отправить
            </Button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <SectionHead
          title="Мои обращения"
          meta={
            queued > 0 ? (
              <>
                Не доставлено: {queued}{' '}
                <Button variant="ghost" size="sm" disabled={retry.saving} onClick={onRetry}>
                  Отправить повторно
                </Button>
              </>
            ) : undefined
          }
        />
        <AsyncState query={list} />
        {!list.loading && !list.error && rows.length === 0 && (
          <EmptyState message="Обращений пока не было." />
        )}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Тема</th>
                  <th>Категория</th>
                  <th>Автор</th>
                  <th>Откуда</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      onClick={() => {
                        setOpenTicket(openTicket === r.id ? null : r.id)
                        setReplyDraft('')
                        sendReply.reset()
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{dateTime(r.createdAt)}</td>
                      <td>{r.subject}</td>
                      <td>{CATEGORY_LABEL[r.category] ?? r.category}</td>
                      <td>{r.authorName}</td>
                      <td>{SOURCE_LABEL[r.source] ?? r.source}</td>
                      <td>
                        {r.status !== 'sent' ? (
                          <span className="muted" title={r.helpdeskError ?? undefined}>
                            не доставлено
                          </span>
                        ) : (
                          <span title={r.helpdeskTicketId ? `№ ${r.helpdeskTicketId}` : undefined}>
                            {supportStatusText(r)}
                          </span>
                        )}
                      </td>
                    </tr>
                    {openTicket === r.id && (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                          {(r.messages ?? []).map((m) => (
                            <div key={m.id} style={{ marginTop: 12 }}>
                              <div className="muted">
                                {m.fromAgent ? m.author : `Вы · ${m.author}`} ·{' '}
                                {dateTime(m.createdAt)}
                                {!m.fromAgent && m.deliveryStatus === 'queued'
                                  ? ' · ещё не доставлено в поддержку'
                                  : ''}
                                {!m.fromAgent && m.deliveryStatus === 'failed'
                                  ? ' · поддержка не приняла ответ'
                                  : ''}
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                            </div>
                          ))}
                          {!(r.messages ?? []).some((m) => m.fromAgent) && (
                            <div className="muted" style={{ marginTop: 12 }}>
                              Поддержка пока не отвечала.
                            </div>
                          )}
                          <div style={{ marginTop: 16 }} onClick={(e) => e.stopPropagation()}>
                            {r.helpdeskResolved && (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                Обращение решено. Если вопрос остался — ответьте, и поддержка
                                откроет его снова.
                              </div>
                            )}
                            <textarea
                              className="input"
                              rows={3}
                              value={replyDraft}
                              onChange={(e) => setReplyDraft(e.target.value)}
                              placeholder="Ваш ответ поддержке"
                              maxLength={5000}
                            />
                            {sendReply.error && <FormError>{sendReply.error}</FormError>}
                            <div style={{ marginTop: 8 }}>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={sendReply.saving || !replyDraft.trim()}
                                onClick={() => void onReply(r)}
                              >
                                {sendReply.saving ? <span className="spin" /> : null}
                                Ответить
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
